import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { supabase } from '../../supabaseClient'
import {
  J, ReflectionBlock, ViewToggle, AiEngagement, FeedbackRounds, handlePrint, fmtDate, fmtDateTime,
  StepReflection, JournalNote, UsageRow, FeedbackRow,
} from '../../components/journal/paper'
import StepReflectionCard from '../../components/journal/StepReflectionCard'

// Step 5 research journal — narrative Journal view (experimental design /
// development plan + write-up) and a Documentation (audit) view (mentor
// review, AI feedback rounds, AI engagement + ISEF disclosure). Mirrors Step
// 3/4's JournalTab on the shared paper kit — last of the 5 phases restoring
// the original Step 5 spec.

interface Payload {
  // Scientific
  design_type?: string; materials?: string; apparatus?: string; procedure?: string
  data_collection_method?: string; data_sheet_url?: string; analysis_plan?: string; error_analysis?: string
  // Engineering (anchored on docs "6.2.2 Engineering Design Document" guide)
  design_description?: string; build_procedure?: string; test_plan?: string
  // Write-Up (both tracks)
  purpose_rq?: string; target_user?: string; variables_hypothesis?: string
  introduction?: string; acknowledgement?: string; references?: string
}
interface ReviewEntry { action: string; note: string | null; acted_at: string }

const AI_LABELS: Record<string, string> = {
  'step5-evaluate-field':     'Design/Write-Up field feedback',
  'step5-mentor-ai-review':   'Mentor AI review',
  'step5-generate-paper':     'Draft paper generation',
  'step5-recommend-forms':    'SRC forms recommendation',
}

const GROUP_LABEL: Record<string, string> = {
  design_type: 'Experimental design type', materials: 'Materials', apparatus: 'Apparatus & software',
  procedure: 'Procedure', data_collection_method: 'Data collection method', analysis_plan: 'Data analysis plan',
  error_analysis: 'Error analysis & improvements',
  design_description: 'Design description', build_procedure: 'Development & testing plan',
  test_plan: 'Performance criteria & iteration ideas',
  purpose_rq: 'Purpose & research question', target_user: 'Target user description',
  variables_hypothesis: 'Variables & hypothesis',
  introduction: 'Introduction', acknowledgement: 'Acknowledgement', references: 'References',
}
function labelForField(field: string): string {
  return GROUP_LABEL[field] ?? field
}

export default function JournalTab({ projectId, studentId }: { projectId: string; studentId: string }) {
  const [payload,    setPayload]    = useState<Payload | null>(null)
  const [status,     setStatus]     = useState<string>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [topic,      setTopic]      = useState<string | null>(null)
  const [notes,      setNotes]      = useState<JournalNote[]>([])
  const [reflection, setReflection] = useState<StepReflection | null>(null)
  const [reviewLog,  setReviewLog]  = useState<ReviewEntry[]>([])
  const [usage,      setUsage]      = useState<UsageRow[]>([])
  const [feedback,   setFeedback]   = useState<FeedbackRow[]>([])
  const [updatedAt,  setUpdatedAt]  = useState<string | null>(null)
  const [track,      setTrack]      = useState<string>('scientific_method')
  const [loading,    setLoading]    = useState(true)
  const [view,       setView]       = useState<'journal' | 'documentation'>('journal')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ data: ed }, { data: summary }, { data: noteRows }, { data: refl }, { data: log }, { data: usageRows }, { data: fbRows }] = await Promise.all([
        supabase.from('experiment_design').select('payload, status, mentor_note, updated_at, track').eq('project_id', projectId).maybeSingle(),
        supabase.from('step1_summary').select('confirmed_topic').eq('project_id', projectId).maybeSingle(),
        supabase.from('journal_note').select('note_id, step_number, source_label, content, saved_at').eq('project_id', projectId).eq('step_number', 5).order('saved_at'),
        supabase.from('step_reflection').select('*').eq('project_id', projectId).eq('step_number', 5).maybeSingle(),
        supabase.from('mentor_review_log').select('action, note, acted_at').eq('project_id', projectId).in('action', ['comment', 'approve']).order('acted_at'),
        supabase.from('ai_usage_log').select('function_name, model, input_tokens, output_tokens').eq('project_id', projectId).like('function_name', 'step5%'),
        supabase.from('ai_feedback_log').select('field, evaluation, created_at').eq('project_id', projectId).eq('step_number', 5).order('created_at'),
      ])
      if (cancelled) return
      setPayload(((ed as any)?.payload ?? {}) as Payload)
      setStatus(((ed as any)?.status ?? 'draft'))
      setMentorNote(((ed as any)?.mentor_note ?? null))
      setUpdatedAt((ed as any)?.updated_at ?? null)
      setTrack(((ed as any)?.track ?? 'scientific_method'))
      setTopic((summary as any)?.confirmed_topic ?? null)
      setNotes((noteRows as JournalNote[] | null) ?? [])
      setReflection((refl as StepReflection | null) ?? null)
      setReviewLog((log as ReviewEntry[] | null) ?? [])
      setUsage((usageRows as UsageRow[] | null) ?? [])
      setFeedback((fbRows as FeedbackRow[] | null) ?? [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [projectId, studentId])

  async function reloadReflection() {
    const { data: refl } = await supabase.from('step_reflection').select('*')
      .eq('project_id', projectId).eq('step_number', 5).maybeSingle()
    setReflection((refl as StepReflection | null) ?? null)
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>

  const p        = payload ?? {}
  const isEng    = track === 'engineering_design'
  const dateStr  = updatedAt ? fmtDate(updatedAt) : fmtDate(new Date().toISOString())
  const complete = status === 'approved_draft' || status === 'completed'
  const isDoc    = view === 'documentation'
  const hasContent = isEng
    ? (p.design_description?.trim() || p.build_procedure?.trim() || p.materials?.trim())
    : (p.design_type?.trim() || p.procedure?.trim() || p.materials?.trim())

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{isDoc ? 'Research Documentation' : 'Your Research Journal'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {isDoc
              ? 'Complete system record for this step — mentor review, AI engagement, and ISEF Rule 4.2 disclosure.'
              : 'A curated record of how you turned your hypothesis into a full experimental design. Print it for your notebook, or copy what\'s meaningful.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ViewToggle value={view} onChange={setView} />
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => handlePrint('journal-step5-print')}>Print</Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <StepReflectionCard projectId={projectId} stepNumber={5} stepLabel={isEng ? 'Step 5: Develop Products' : 'Step 5: Design Experiments'} onSavedChange={() => { reloadReflection() }} />
      </Box>

      <J.Page id="journal-step5-print">
        <J.Header
          title={isDoc ? 'STEP 5 DOCUMENTATION' : (isEng ? 'PRODUCT DEVELOPMENT JOURNAL' : 'EXPERIMENTAL DESIGN JOURNAL')}
          subtitle={isEng
            ? (isDoc ? 'Complete record — Develop Products' : 'Step 5 — Develop Products')
            : (isDoc ? 'Complete record — Design Experiments' : 'Step 5 — Design Experiments')}
          date={dateStr}
        />

        {topic && (<><J.Section>MY TOPIC</J.Section><J.Quote>{topic}</J.Quote></>)}

        {p.purpose_rq?.trim() && (
          <><J.Section>{isEng ? 'PROBLEM STATEMENT & PURPOSE' : 'PURPOSE & RESEARCH QUESTION'}</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.purpose_rq}</J.Body></>
        )}
        {isEng && p.target_user?.trim() && (
          <><J.Section>TARGET USER DESCRIPTION</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.target_user}</J.Body></>
        )}
        {p.variables_hypothesis?.trim() && (
          <><J.Section>{isEng ? 'USER NEEDS & MVP' : 'VARIABLES & HYPOTHESIS'}</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.variables_hypothesis}</J.Body></>
        )}

        {isEng ? (<>
          {p.design_description?.trim() && (<><J.Section>DESIGN DESCRIPTION</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.design_description}</J.Body></>)}
          {p.materials?.trim() && (<><J.Section>MATERIALS & TOOLS</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.materials}</J.Body></>)}
          {p.build_procedure?.trim() && (<><J.Section>DEVELOPMENT & TESTING PLAN</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.build_procedure}</J.Body></>)}
          {p.test_plan?.trim() && (<><J.Section>PERFORMANCE CRITERIA & ITERATION IDEAS</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.test_plan}</J.Body></>)}
        </>) : (<>
          {p.design_type?.trim() && (<><J.Section>EXPERIMENTAL DESIGN TYPE</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.design_type}</J.Body></>)}
          {p.materials?.trim() && (<><J.Section>MATERIALS</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.materials}</J.Body></>)}
          {p.apparatus?.trim() && (<><J.Section>APPARATUS & SOFTWARE</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.apparatus}</J.Body></>)}
          {p.procedure?.trim() && (<><J.Section>PROCEDURE</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.procedure}</J.Body></>)}
          {(p.data_collection_method?.trim() || p.data_sheet_url?.trim()) && (
            <Box className="journal-no-break">
              <J.Section>DATA COLLECTION METHOD</J.Section>
              {p.data_collection_method?.trim() && <J.Body sx={{ whiteSpace: 'pre-wrap', mb: p.data_sheet_url?.trim() ? 0.5 : 0 }}>{p.data_collection_method}</J.Body>}
              {p.data_sheet_url?.trim() && (<><J.Label>Data table</J.Label><J.Body sx={{ mb: 0 }}>{p.data_sheet_url}</J.Body></>)}
            </Box>
          )}
          {p.analysis_plan?.trim() && (<><J.Section>DATA ANALYSIS PLAN</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.analysis_plan}</J.Body></>)}
          {p.error_analysis?.trim() && (<><J.Section>ERROR ANALYSIS & IMPROVEMENTS</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.error_analysis}</J.Body></>)}
        </>)}

        {p.introduction?.trim() && (<><J.Section>INTRODUCTION</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.introduction}</J.Body></>)}
        {p.acknowledgement?.trim() && (<><J.Section>ACKNOWLEDGEMENT</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.acknowledgement}</J.Body></>)}
        {p.references?.trim() && (<><J.Section>REFERENCES</J.Section><J.Body sx={{ whiteSpace: 'pre-wrap' }}>{p.references}</J.Body></>)}

        <ReflectionBlock refl={reflection} notes={notes} />

        {isDoc && (mentorNote || reviewLog.length > 0) && (
          <>
            <J.Section>MENTOR REVIEW RECORD</J.Section>
            {reviewLog.map((r, i) => (
              <Box key={i} className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>{r.action === 'approve' ? 'Approved' : 'Sent back with comments'} · {fmtDateTime(r.acted_at)}</J.Label>
                {r.note && <J.Quote>{r.note}</J.Quote>}
              </Box>
            ))}
            {mentorNote && reviewLog.length === 0 && <J.Quote>{mentorNote}</J.Quote>}
          </>
        )}

        {isDoc && <FeedbackRounds rows={feedback.map(r => ({ ...r, field: labelForField(r.field) }))} fieldLabels={{}} />}
        {isDoc && <AiEngagement usage={usage} labels={AI_LABELS} date={dateStr} />}

        {complete && <J.Signatures />}
        {!complete && (
          <J.Body sx={{ mt: 2, fontSize: '10pt', color: '#555', fontStyle: 'italic' }}>
            This entry updates as you refine your work. The final version — with signatures — is ready once your mentor approves this step.
          </J.Body>
        )}
      </J.Page>

      {!topic && !hasContent && (
        <Alert severity="info" sx={{ mt: 2 }}>Your Step 5 journal fills in as you complete your {isEng ? 'development plan' : 'experimental design'}.</Alert>
      )}
    </Box>
  )
}
